import { useState, useMemo } from "react";
import { Flex } from "@chakra-ui/react";
import { useBreadcrumb } from "features/breadcrumbs";
import ListingPageHeaderBar from "ui/components/Page/ListingPageHeaderBar";
import { usePortariaAutorizacao } from "features/portaria/hooks";
import { useLoading } from "components/GlobalLoading";
import useFiltro from "./hooks/useFiltro";
import ModalFiltroPortaria from "./components/ModalFiltroPortaria";
import { useDebounce } from "features/shared/useDebouce";
import { useCurrentTenant } from "features/auth/tenant.store";
import Container from "components/Container";
import CardPortaria from "./components/CardPortaria";
import TableTags from "./components/TableTags";
import PageHeaderPortaria from "./components/PageHeaderPortaria";
import OutlineButton from "ui/components/Button/OutlineButton";
import ModalConsultarPessoasEUnidades from "components/ModalConsultarPessoasEUnidades";
import InfiniteScrollCardList from "ui/components/InfiniteScrollCardList";
import { AutorizacaoItem } from "core/features/portaria/typings";
import ModalListaConvidados from "ui/components/ModalPortariaPessoas";
import { PermissaoEnum } from "core/resources/enums/permissao";
import ExportMenu from "components/ExportMenu";
import { useCallback } from "react";
import { trackEvent } from "infra/helpers/event";
import { RelatorioPortariaEvents } from "core/features/relatorios/enums/events";
import useRelatorioPortariaPdf from "core/features/portaria/hooks/useRelatorioPortariaPdf";
import { exportExcelRelatorioPortaria } from "./util/helper";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import PDFRelatorioPortaria from "./components/PDFRelatorio";
import ModalConfirmarDadosPessoa from "./components/ModalConfirmarDadosPessoa";
import useConfirmarAutorizacaoPortaria from "core/features/portaria/hooks/useConfirmarAutorizacaoPortaria";
import ModalParametrosPortaria from "./components/ModalParametrosPortaria";
import { useWindowSize } from "infra/helpers/window";

function PortariaListagemPage() {
  const { width } = useWindowSize();
  const [isOpenModalConsultaUnidades, setIsOpenModalConsultaUnidades] =
    useState(false);
  const [idConfirmarPessoaPortaria, setIdConfirmarPessoaPortaria] = useState<
    string | null
  >(null);
  const [isOpenModalFiltros, setIsOpenModalFiltros] = useState(false);
  const [isOpenModalParametros, setIsOpenModalParametros] = useState(false);
  const [idModalListaPessoas, setIdModalPortariaPessoas] = useState<{
    eventoId: string;
    portariaId: string;
  }>();

  const { tenant } = useCurrentTenant();

  useBreadcrumb([
    { name: "Administração", url: "/administracao" },
    { name: "Portaria" },
  ]);

  const { filtro, onChangeFiltro } = useFiltro();

  const debouncedSearch = useDebounce(filtro.search);

  const { isLoading: isLoadingPdf, mutate: handleRelatorioPdf } =
    useRelatorioPortariaPdf(filtro);

  const {
    data,
    isLoading,
    refetch,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = usePortariaAutorizacao({
    ...filtro,
    search: debouncedSearch,
  });
  const numFiltros =
    (filtro.dataInicio || filtro.dataFim ? 1 : 0) +
    (filtro.pessoaId ? 1 : 0) +
    (filtro.unidadeId ? 1 : 0) +
    (filtro.tipoAutorizacaoId ? 1 : 0) +
    (filtro.verConfirmadas === "1" ? 1 : 0);

  const autorizacoes = useMemo(() => {
    return (
      data?.pages.reduce(
        (agg: AutorizacaoItem[], item) => agg.concat(item.items ?? []),
        [],
      ) ?? []
    );
  }, [data]);

  const _onExportExcel = useCallback(async () => {
    trackEvent(RelatorioPortariaEvents.ExportarExcel);
    handleRelatorioPdf({} as any, {
      onSuccess: (data) => {
        exportExcelRelatorioPortaria({
          data,
        });
      },
    });
  }, [handleRelatorioPdf]);

  const { onDownload: onExportPdf } = useDownloadRenderedPdf({
    fileName: `Relatório de Portarias Confirmadas.pdf`,
    Component: PDFRelatorioPortaria,
    loading: isLoadingPdf,
  });

  const _onExportPdf = useCallback(async () => {
    trackEvent(RelatorioPortariaEvents.ExportarPDF);
    handleRelatorioPdf({} as any, {
      onSuccess: (data) => {
        onExportPdf(data);
      },
    });
  }, [handleRelatorioPdf, onExportPdf]);

  const { mutate: confirmar, isLoading: isLoadingConfirmar } =
    useConfirmarAutorizacaoPortaria(
      () => {
        refetch?.();
      },
      () => {},
    );

  useLoading(isLoading || isLoadingPdf);

  const portariaPessoaId = autorizacoes.find(
    (item) => item.id === idConfirmarPessoaPortaria,
  )?.pessoa?.id;

  const RenderCard = useCallback(
    ({ item }: { item: AutorizacaoItem }) => {
      return (
        <CardPortaria
          key={item.id}
          item={item}
          refetch={refetch}
          onShowListaConvidadosPortaria={setIdModalPortariaPessoas}
          onConfirmarDadosPortaria={setIdConfirmarPessoaPortaria}
          onShowOutrasPessoas={setIdModalPortariaPessoas}
        />
      );
    },
    [refetch],
  );

  return (
    <Container>
      <PageHeaderPortaria
        filtro={filtro}
        onChangeFiltro={onChangeFiltro}
        onOpenParametros={() => setIsOpenModalParametros(true)}
      />

      <ListingPageHeaderBar
        valueSearch={filtro.search}
        onSearch={(q?: string) => onChangeFiltro({ search: q })}
        visibleFiltro
        badge
        badgeValue={numFiltros}
        onFiltro={() => setIsOpenModalFiltros!(true)}
        wrapChildren
        extraModalButton={
          <Flex gap={4} justifyContent="flex-end" flex={1}>
            <ExportMenu
              onExportPdf={_onExportPdf}
              onExportarXlsx={_onExportExcel}
              text="Exportar relatório"
            />
            <OutlineButton
              permissions={[
                PermissaoEnum.Backoffice.Administrativo.Portaria
                  .ConsultarUnidadesEPessoas,
              ]}
              onClick={() => setIsOpenModalConsultaUnidades(true)}
            >
              Consultar unidades e pessoas
            </OutlineButton>
          </Flex>
        }
        children={
          <Flex flexWrap="wrap">
            <TableTags
              value={filtro.tipoAutorizacaoId}
              onChange={(v) => onChangeFiltro({ tipoAutorizacaoId: v })}
            />
          </Flex>
        }
      />

      <Flex flex={1} mt="32px" flexDir="column">
        <InfiniteScrollCardList
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          data={autorizacoes}
          hasMore={!!hasNextPage}
          onNext={fetchNextPage}
          emptyDescription="Não há portarias cadastradas"
          RenderCard={RenderCard}
          defaultCardSize={width <= 1400 ? 175 : 150}
          itemMarginBottom={10}
        />
      </Flex>

      {idModalListaPessoas?.eventoId ? (
        <ModalListaConvidados
          onClose={() => setIdModalPortariaPessoas(undefined)}
          portariaId={idModalListaPessoas.portariaId}
          eventoId={idModalListaPessoas.eventoId}
          visible={!!idModalListaPessoas}
        />
      ) : null}

      {isOpenModalFiltros ? (
        <ModalFiltroPortaria
          tenantId={tenant.id}
          visible={isOpenModalFiltros}
          onClose={() => setIsOpenModalFiltros(false)}
          filtro={filtro}
          onChangeFiltro={onChangeFiltro}
        />
      ) : null}

      {idConfirmarPessoaPortaria ? (
        <ModalConfirmarDadosPessoa
          portariaPessoaId={portariaPessoaId}
          portariaAutorizacaoId={idConfirmarPessoaPortaria}
          visible={!!idConfirmarPessoaPortaria}
          onClose={() => setIdConfirmarPessoaPortaria(null)}
          onSubmit={(form) =>
            confirmar(
              {
                id: idConfirmarPessoaPortaria,
                body: form,
              },
              {
                onSuccess: () => setIdConfirmarPessoaPortaria(null),
              },
            )
          }
          isLoadingConfirmar={isLoadingConfirmar}
        />
      ) : null}

      <ModalConsultarPessoasEUnidades
        visible={isOpenModalConsultaUnidades}
        onClose={() => setIsOpenModalConsultaUnidades(false)}
      />

      {isOpenModalParametros ? (
        <ModalParametrosPortaria
          visible={isOpenModalParametros}
          onClose={() => setIsOpenModalParametros(false)}
        />
      ) : null}
    </Container>
  );
}
export default PortariaListagemPage;
